import React, { useState } from 'react';
import { Play, Plus, Trash2, ChevronDown, ChevronRight, Key } from 'lucide-react';

interface StartProcessTabProps {
    data: any;
    onChange: (key: string, value: any) => void;
}

const inputCls = "w-full px-2 py-1.5 text-xs border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none";
const labelCls = "block text-[10px] font-bold text-slate-500 uppercase mb-1";

const KeyValueEditor: React.FC<{
    value: Record<string, string>;
    onChange: (val: Record<string, string>) => void;
}> = ({ value, onChange }) => {
    const entries = Object.entries(value || {});

    const handleAdd = () => onChange({ ...value, '': '' });

    const handleRemove = (oldKey: string) => {
        const next = { ...value };
        delete next[oldKey];
        onChange(next);
    };

    const handleChange = (oldKey: string, newKey: string, newVal: string) => {
        const next: Record<string, string> = {};
        for (const [k, v] of Object.entries(value || {})) {
            if (k === oldKey) next[newKey] = newVal;
            else next[k] = v;
        }
        onChange(next);
    };

    return (
        <div className="space-y-1.5">
            {entries.length > 0 && (
                <div className="grid grid-cols-[1fr_1fr_28px] gap-1.5 text-[9px] font-bold text-slate-400 uppercase px-0.5">
                    <span>Key</span><span>Value</span><span />
                </div>
            )}
            {entries.map(([k, v], idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_28px] gap-1.5 items-center">
                    <input type="text" value={k} onChange={(e) => handleChange(k, e.target.value, v)} placeholder="key" className={`${inputCls} font-mono`} />
                    <input type="text" value={v} onChange={(e) => handleChange(k, k, e.target.value)} placeholder="value" className={inputCls} />
                    <button onClick={() => handleRemove(k)} className="text-slate-400 hover:text-red-500 transition-colors p-1"><Trash2 size={12} /></button>
                </div>
            ))}
            <button onClick={handleAdd} className="text-[10px] text-teal-600 font-bold hover:bg-teal-50 dark:hover:bg-teal-900/30 px-2 py-1 rounded transition-colors flex items-center gap-1">
                <Plus size={10} /> Add
            </button>
        </div>
    );
};

export const StartProcessTab: React.FC<StartProcessTabProps> = ({ data, onChange }) => {
    const [showExtensions, setShowExtensions] = useState(false);
    const extCount = data.startExtensions ? Object.keys(data.startExtensions).length : 0;

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-900 flex gap-3">
                <Play className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">Start Process Configuration</p>
                    <p className="text-[11px] text-emerald-700 dark:text-emerald-400 leading-relaxed">
                        Configure how the PSS host initiates this workflow — view, permissions, wizard step, and deduplication.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                {/* View */}
                <div>
                    <label className={labelCls}>Start View</label>
                    <input
                        type="text"
                        value={data.startView || 'Workflow::GenericStartView'}
                        onChange={(e) => onChange('startView', e.target.value)}
                        placeholder="Workflow::GenericStartView"
                        className={inputCls}
                    />
                    <p className="text-[10px] text-slate-400 mt-1">UI component that renders the start form.</p>
                </div>

                {/* Roles + Step */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Allowed Roles</label>
                        <input
                            type="text"
                            value={data.startRoles || '*'}
                            onChange={(e) => onChange('startRoles', e.target.value)}
                            placeholder="* (all roles)"
                            className={inputCls}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Comma-separated roles or * for all.</p>
                    </div>
                    <div>
                        <label className={labelCls}>Step</label>
                        <input
                            type="text"
                            value={data.startStep || '1'}
                            onChange={(e) => onChange('startStep', e.target.value)}
                            placeholder="1"
                            className={inputCls}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Leave empty to hide in wizard.</p>
                    </div>
                </div>

                {/* Mode + ActorType */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Mode</label>
                        <select
                            value={data.startMode || 'end'}
                            onChange={(e) => onChange('startMode', e.target.value)}
                            className={inputCls}
                        >
                            <option value="end">End</option>
                            <option value="continue">Continue (redirect to next task)</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Actor Type</label>
                        <input
                            type="text"
                            value={data.startActorType || ''}
                            onChange={(e) => onChange('startActorType', e.target.value)}
                            placeholder="Optional"
                            className={inputCls}
                        />
                    </div>
                </div>

                {/* Deduplication */}
                <div>
                    <label className={labelCls}>Deduplication Key</label>
                    <input
                        type="text"
                        value={data.startDeduplication || ''}
                        onChange={(e) => onChange('startDeduplication', e.target.value)}
                        placeholder="JSON path template (leave empty for none)"
                        className={`${inputCls} font-mono`}
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Prevents duplicate workflow instances when set.</p>
                </div>

                {/* Extensions (collapsible) */}
                <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                    <button
                        onClick={() => setShowExtensions(!showExtensions)}
                        className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 uppercase tracking-wider transition-colors"
                    >
                        {showExtensions ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                        <Key size={10} />
                        Extensions
                        {extCount > 0 && (
                            <span className="bg-slate-200 dark:bg-slate-800 text-slate-500 px-1.5 rounded-full text-[9px] normal-case">{extCount}</span>
                        )}
                    </button>
                    {showExtensions && (
                        <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200">
                            <KeyValueEditor
                                value={data.startExtensions || {}}
                                onChange={(val) => onChange('startExtensions', val)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
